import { FileText, Plus } from "lucide-react";

import type { JDCompetency, JDContent, JDKra } from "../../utils/jobDescription";
import type { JobDescriptionInfo } from "./JobDescriptionPanel";

/** The job description kept in PME People, if the appraisal has one. */
export function onlineJd(jd: JobDescriptionInfo | null | undefined): JDContent | null {
  return jd?.online?.content ?? null;
}

const norm = (s: string) => s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, " ").trim();

function matches(a: string, b: string): boolean {
  const x = norm(a);
  const y = norm(b);
  if (!x || !y) return false;
  return x === y || (x.length > 3 && y.includes(x)) || (y.length > 3 && x.includes(y));
}

/** The key result area an appraisal row is about (by name). */
export function findKra(jd: JDContent | null, area: string): JDKra | null {
  if (!jd) return null;
  return jd.kras.find((k) => matches(k.area, area)) ?? null;
}

/** The competency an appraisal behaviour is about (by name). */
export function findCompetency(jd: JDContent | null, name: string): JDCompetency | null {
  if (!jd) return null;
  return jd.competencies.find((c) => matches(c.name, name)) ?? null;
}

/** Under a responsibility: what the job description says about this area. */
export function JdAreaHint({ kra }: { kra: JDKra | null }) {
  if (!kra) return null;
  const measures = kra.measures.filter((m) => m.text.trim());
  if (!kra.covers && measures.length === 0 && kra.tasks.length === 0) return null;
  return (
    <div className="mb-2 rounded-lg border border-pme-blue/15 bg-pme-sky/50 px-3 py-2 text-xs text-gray-700">
      <p className="flex items-center gap-1.5 font-semibold text-pme-navy">
        <FileText size={13} aria-hidden /> From the job description
        {kra.weighting != null && <span className="font-normal text-gray-500">· {kra.weighting}% of the role</span>}
      </p>
      {kra.covers && <p className="mt-1">{kra.covers}</p>}
      {measures.length > 0 && (
        <>
          <p className="mt-1.5 font-semibold text-gray-600">Measured by</p>
          <ul className="mt-0.5 list-disc space-y-0.5 pl-4">
            {measures.map((m, i) => (
              <li key={i}>
                {m.text}
                {m.proposed && <span className="text-gray-500"> (to be agreed)</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** Under a behaviour: what good looks like, from the job description. */
export function JdCompetencyHint({ competency }: { competency: JDCompetency | null }) {
  if (!competency?.description) return null;
  return (
    <p className="mb-2 rounded-lg border border-pme-blue/15 bg-pme-sky/50 px-3 py-2 text-xs text-gray-700">
      <span className="font-semibold text-pme-navy">What good looks like: </span>
      {competency.description}
    </p>
  );
}

/** Measures from the job description, offered as objectives. Measures not yet
 *  agreed come first: the appraisal is where they get agreed. */
export function JdObjectiveIdeas({
  jd,
  taken,
  onAdd,
}: {
  jd: JDContent | null;
  taken: string[];
  onAdd: (objective: string, howMeasured: string) => void;
}) {
  if (!jd) return null;
  const used = new Set(taken.map(norm));
  const ideas = jd.kras
    .flatMap((k) => k.measures.filter((m) => m.text.trim()).map((m) => ({ area: k.area, text: m.text, proposed: m.proposed })))
    .filter((i) => !used.has(norm(i.text)))
    .sort((a, b) => Number(b.proposed) - Number(a.proposed));
  if (ideas.length === 0) return null;
  return (
    <div className="mb-4 rounded-lg border border-pme-blue/15 bg-pme-sky/40 p-3">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-pme-navy">
        <FileText size={13} aria-hidden /> Ideas from the job description
      </p>
      <p className="mt-0.5 text-xs text-gray-600">Tap one to add it as an objective. Ones marked “to be agreed” are new measures to agree in the meeting.</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {ideas.slice(0, 12).map((i, n) => (
          <button
            key={n}
            type="button"
            onClick={() => onAdd(i.text, `${i.area}: as set out in the job description`)}
            className="inline-flex min-h-[32px] max-w-full items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1 text-left text-xs text-gray-700 hover:border-pme-blue hover:text-pme-navy"
          >
            <Plus size={12} aria-hidden className="shrink-0" />
            <span className="min-w-0">{i.text}</span>
            {i.proposed && <span className="shrink-0 text-gray-400">· to be agreed</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
